import { DayDetailTracker, DayDetailTrackerResponse, DynamicChecklists, EcoCalendarEvent, EndofDaySummaryAPI, FormFieldConfig, Journal, PositionTimeLine, PostTradeDataAPI, PostTradeTooltipAPIResponse, PreTradeJournalPayload, TradeExecution, TradingJournalResponse, UpdateDailyJournalPayload, UploadFileResponse } from "./type";
import { API_URL } from "env";

async function getDailyChecklist(date: string): Promise<TradingJournalResponse> {
    try {
        const res = await fetch(`${API_URL}/api/v1/be/checklist?date=${date}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${localStorage.getItem("token")}`,
            },
        });
        const data: TradingJournalResponse = await res.json();
        return data;
    } catch (error) {
        console.error(error);
        throw new Error("Failed to fetch daily checklist");
    }
}

async function updateDailyChecklist(payload: UpdateDailyJournalPayload) {
    try {
        const res = await fetch(`${API_URL}/api/v1/be/checklist/journal`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${localStorage.getItem("token")}`,
            },
            body: JSON.stringify(payload),
        });
        const data = await res.json();
        return data;
    } catch (error) {
        console.error(error);
        throw new Error("Failed to update daily checklist");
    }
}

async function uploadPreTradeChecklist(payload: PreTradeJournalPayload) {
    try {
        const res = await fetch(`${API_URL}/api/v1/be/checklist/pretrade`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${localStorage.getItem("token")}`,
            },
            body: JSON.stringify(payload),
        });
        const data = await res.json();
        return data;
    } catch (error) {
        console.error(error);
        throw new Error("Failed to upload pre-trade checklist");
    }
}

async function getLiveTradeData(account_number: string) {
    //console.warn("On debug mode: Remove dateFrom hardcode when testing in production: File: src/pages/Academy/ThirteenWeeks/DayDetail/function.tsx:getLiveTradeData");
    try {
        const today = new Date();
        const dateFrom = today.toISOString().split('T')[0]; // Format: YYYY-MM-DD
        //const dateFrom = "2026-06-03"
        const res = await fetch(`${API_URL}/api/v1/live-trading?account_number=${account_number}&date_from=${dateFrom}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${localStorage.getItem("token")}`,
            },
        });
        const data = await res.json();
        return data.data;
    } catch (error) {
        console.error(error);
        throw new Error("Failed to fetch live trade data");
    }
}

async function getHistoryTradeData(account_number: string) {
    try {
        const today = new Date();
        const dateFrom = today.toISOString().split('T')[0]; // Format: YYYY-MM-DD &date_to=${dateFrom}
        //const dateFrom = "2026-06-09"
        const res = await fetch(`${API_URL}/api/v1/trade-history?account_number=${account_number}&date_from=${dateFrom}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${localStorage.getItem("token")}`,
            },
        });
        const data = await res.json();
        return data.data;
    } catch (error) {
        console.error(error);
        throw new Error("Failed to fetch history trade data");
    }
}

async function getAccountList() {
    try {
        const res = await fetch(`${API_URL}/api/v1/be/teams/account?per_page=100`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${localStorage.getItem("token")}`,
            },
        });
        const data = await res.json();
        return data.trade_account || [];
    }
    catch (error) {
        console.error(error);
        throw new Error("Failed to fetch account list");
    }
}

async function uploadPicture(file: File): Promise<UploadFileResponse> {
    try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await fetch(`${API_URL}/api/v1/be/documents/upload`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${localStorage.getItem("token")}`,
            },
            body: formData,
        });
        const data = await res.json();
        return data;
    } catch (error) {
        console.error(error);
        throw new Error("Failed to upload picture");
    }
}

async function streamingUploadPicture(id: number) {
    try {
        const res = await fetch(`${API_URL}/api/v1/be/documents/${id}/stream`, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${localStorage.getItem("token")}`,
            },
        });
        return res;
    } catch (error) {
        console.error(error);
        throw new Error("Failed to stream upload picture");
    }
}

export interface SectionStatus {
    id: number;
    label: string;
    isCompleted: boolean;
}

export interface DayCompletionStatus {
    sod: SectionStatus[];
    before_entry: SectionStatus[];
    eod: SectionStatus[];
}

/**
 * Helper to calculate a completeness score for a trade execution.
 * Aligned with the scoring system in VerdictBanner.
 */
function getExecutionCompletenessScore(execution: any, dynamicChecklist?: DynamicChecklists) {
    if (!execution) return { total: 0, market: 0, setup: 0, risk: 0, psych: 0 };
    const preTrade = execution.pre_trade_data;
    const checklistRecords = execution.checklist_records || [];

    let market = 0;
    // 1. Market Context (Max 20)
    if (preTrade?.pre_main_bias && preTrade?.bias_reason) market += 5;
    if (preTrade?.key_levels && preTrade.key_levels.length > 0 && preTrade.key_levels.some((kl: any) => kl.price_zone && kl.zone_type)) market += 4;
    if (preTrade?.pre_liquidity_zone && preTrade?.pre_liquidity_zone !== 'Chưa rõ') market += 4;
    if (preTrade?.scenario_guess?.trim()) market += 4;
    if (preTrade?.pre_market_condition) market += 2;
    if (preTrade?.pre_trading_session) market += 1;

    let setup = 0;
    // 2. Setup Quality (Max 30)
    const setupItems = dynamicChecklist?.pre_trade?.filter(i => i.order_index >= 1 && i.order_index <= 3) || [];
    setupItems.forEach((item) => {
        const isChecked = checklistRecords.find((r: any) => r.item_id === item.id)?.is_checked;
        if (isChecked) {
            if (item.order_index === 1) setup += 10;
            else if (item.order_index === 2) setup += 8;
            else if (item.order_index === 3) setup += 6;
        }
    });
    if (preTrade?.prior_trade_state) setup += 6;

    let risk = 0;
    // 3. Risk Management (Max 35)
    const riskItems = dynamicChecklist?.pre_trade?.filter(i => i.order_index > 3) || [];
    const riskCheckedCount = riskItems.filter(item =>
        checklistRecords.find((r: any) => r.item_id === item.id)?.is_checked
    ).length;
    risk = Math.min(35, riskCheckedCount * 2.5);

    let psych = 0;
    // 4. Psychology (Max 15)
    const goodEmotions = ['p_calm', 'p_conf', 'p_focus', 'p_patient', 'p_disc', 'p_relax', 'p_obj'];
    const hasGoodEmotion = preTrade?.pre_emotions?.some((e: any) => goodEmotions.includes(String(e)));
    if (hasGoodEmotion) psych += 5;
    if (preTrade?.sl_loss_percent && preTrade?.dd_left_percent) psych += 5;
    if (preTrade?.risk_acceptance === 'accept') psych += 5;

    return {
        total: market + setup + risk + psych,
        market,
        setup,
        risk,
        psych
    };
}

/**
 * Finds the most "complete" execution from a list, based on the weighted scoring criteria.
 */
function getMostCompleteExecution(executions: TradeExecution[], dynamicChecklist?: DynamicChecklists): TradeExecution | undefined {
    if (!executions || executions.length === 0) return undefined;
    if (executions.length === 1) return executions[0];

    return [...executions].sort((a, b) =>
        getExecutionCompletenessScore(b, dynamicChecklist).total - getExecutionCompletenessScore(a, dynamicChecklist).total
    )[0];
}

/**
 * Calculates completion status for all sections across Start of Day, Before Entry, and End of Day.
 * Follows the logic defined in each corresponding component.
 */
function getJournalCompletionStatus(
    journal: Journal,
    dynamicChecklist?: DynamicChecklists,
    currentTradeExecution?: TradeExecution
): DayCompletionStatus {
    // --- 1. START OF DAY (SOD) ---
    const sod: SectionStatus[] = [];

    // ... rest of the code ...

    // Section 1: Mục tiêu & Quản trị rủi ro
    const riskSod = !!journal.psychology_market_condition && !!journal.target_action;
    sod.push({ id: 1, label: "Mục tiêu & Quản trị rủi ro", isCompleted: riskSod });

    // Section 2: Kiểm tra Thể trạng & Sức khỏe (All rating items > 0)
    const ph = journal.physical_health_tags;
    const healthSod = !!ph && [
        ph.sleep_quality,
        ph.mental_alertness,
        ph.physical_comfort,
        ph.no_distractions,
        ph.quiet_workspace
    ].every(v => !!v && v !== "0");
    sod.push({ id: 2, label: "Thể trạng & Sức khỏe", isCompleted: healthSod });

    // Section 3: Bối cảnh thị trường (SOD)
    const tc = journal.technical_context;
    const techFilledSod = !!(tc?.structure_d1 && tc?.structure_h4 && tc?.alignment_h1_m15);
    const keyFilledSod = (journal.key_level_zone?.length > 0);
    const scoreSod = [
        techFilledSod,
        keyFilledSod,
        !!journal.liquidity_status,
        !!journal.scenario_guess?.trim(),
        !!journal.market_cleanliness,
        !!journal.trading_session
    ].filter(Boolean).length;
    sod.push({ id: 3, label: "Bối cảnh thị trường", isCompleted: scoreSod >= 6 });

    // Section 4: Lịch tin kinh tế (Commented as per user request)
    // const newsSod = journal.news_events?.length > 0;
    // sod.push({ id: 4, label: "Lịch tin kinh tế", isCompleted: newsSod });

    // Section 7: Dự báo kịch bản phiên
    const scenarios = journal.planned_setups?.filter(s => s.setup_type === 'SCENARIO') || [];
    const scenarioSod = scenarios.length > 0 && scenarios.every(s => !!s.price_behavior_desc?.trim() && !!s.action_notes?.trim());
    sod.push({ id: 7, label: "Dự báo kịch bản", isCompleted: scenarioSod });


    // --- 2. BEFORE ENTRY (BE) ---
    const before_entry: SectionStatus[] = [];
    // If no specific execution is provided, pick the most complete matching one to show status.
    const activeExecution = currentTradeExecution || getMostCompleteExecution(journal.trade_executions || [], dynamicChecklist);

    const preTrade = activeExecution?.pre_trade_data;
    const checklistRecords = activeExecution?.checklist_records || [];

    // Section 1: Bối cảnh thị trường (BE)
    const keyFilledBe = (preTrade?.key_levels?.length || 0) > 0;
    const scoreBe = [
        !!preTrade?.pre_main_bias,
        keyFilledBe,
        !!preTrade?.pre_liquidity_zone,
        !!preTrade?.scenario_guess?.trim(),
        !!preTrade?.pre_market_condition,
        (preTrade?.pre_trading_session?.length || 0) > 0
    ].filter(Boolean).length;
    before_entry.push({ id: 1, label: "Bối cảnh thị trường", isCompleted: scoreBe >= 6 });

    // Section 2: Chất lượng Setup (logic: Dynamic items with order_index <= 3, checked > 0)
    const setupItemIds = dynamicChecklist?.pre_trade?.filter(i => i.order_index <= 3).map(i => i.id) || [];
    const setupChecked = checklistRecords.filter(r => setupItemIds.includes(r.item_id) && r.is_checked).length;
    before_entry.push({ id: 2, label: "Chất lượng Setup", isCompleted: setupChecked > 0 });

    // Section 3: Quản trị rủi ro (logic: Dynamic items with order_index > 3, checked > 0)
    const riskItemIds = dynamicChecklist?.pre_trade?.filter(i => i.order_index > 3).map(i => i.id) || [];
    const riskChecked = checklistRecords.filter(r => riskItemIds.includes(r.item_id) && r.is_checked).length;
    before_entry.push({ id: 3, label: "Quản trị rủi ro", isCompleted: riskChecked > 0 });

    // Section 4: Tâm lý giao dịch
    const psychBe = !!preTrade && (preTrade.pre_emotions?.length > 0) && !!preTrade.sl_loss_percent && !!preTrade.dd_left_percent && !!preTrade.risk_acceptance;
    before_entry.push({ id: 4, label: "Tâm lý giao dịch", isCompleted: !!psychBe });


    // --- 3. END OF DAY (EOD) ---
    const eod: SectionStatus[] = [];

    let hasLessonsAndCommitment = false;

    if (journal.eod_extra_data) {
        try {
            const extra = typeof journal.eod_extra_data === 'string'
                ? JSON.parse(journal.eod_extra_data)
                : journal.eod_extra_data;

            let noteBrokenPlan = '';
            let noteDeviation = '';
            let noteCommitment = '';

            if (extra?.summary_note) {
                try {
                    const parsedSummaryNote = typeof extra.summary_note === 'string'
                        ? JSON.parse(extra.summary_note)
                        : extra.summary_note;

                    const eodReview = parsedSummaryNote?.eod_review_data;
                    if (eodReview) {
                        noteBrokenPlan = eodReview.note_broken_plan || '';
                        noteDeviation = eodReview.note_deviation || '';
                        noteCommitment = eodReview.note_commitment || '';
                    }
                } catch (e) {
                    console.error("Failed to parse summary_note in completion status", e);
                }
            }

            // Fallback sang worst_thing / best_thing nếu chưa bóc tách được từ eod_review_data
            if (!noteBrokenPlan && extra?.worst_thing) {
                noteBrokenPlan = extra.worst_thing;
            }
            if (!noteCommitment && extra?.best_thing) {
                noteCommitment = extra.best_thing;
            }

            hasLessonsAndCommitment = !!(noteBrokenPlan?.trim() || noteDeviation?.trim() || noteCommitment?.trim());
        } catch (e) {
            console.error("Failed to parse eod_extra_data in completion status", e);
        }
    }

    // Chỉ kiểm tra 1 mục check trạng thái cuối ngày
    eod.push({ id: 1, label: "Bài học & cam kết", isCompleted: hasLessonsAndCommitment });

    return { sod, before_entry, eod };
}

async function getTradePositionHistory(account_number: string, date?: string): Promise<DayDetailTracker[] | undefined> {
    try {
        const queryParams = new URLSearchParams();
        if (date) queryParams.append("date", date);
        const res = await fetch(`${API_URL}/api/v1/be/checklist/timelines?${queryParams.toString()}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${localStorage.getItem("token")}`,
            },
        });
        const data: DayDetailTrackerResponse = await res.json();
        return data.data || [];
    }
    catch (error) {
        console.error(error);
    }
}

async function getPostTradeJournalHistory(account_number: string, search?: string, filter?: "WIN" | "LOSS", date?: string): Promise<PostTradeDataAPI | undefined> {
    try {
        const queryParams = new URLSearchParams();
        queryParams.append("account_number", account_number);
        if (search) queryParams.append("search", search);
        if (filter) queryParams.append("filter", filter);
        if (date) queryParams.append("date", date);
        const res = await fetch(`${API_URL}/api/v1/be/checklist/post-trade?${queryParams.toString()}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${localStorage.getItem("token")}`,
            },
        });
        const data: PostTradeDataAPI = await res.json();
        return data || undefined;
    }
    catch (error) {
        console.error(error);
    }
}

async function getEcoCalendar(from: string, to: string, tag?: string, currency?: string): Promise<EcoCalendarEvent[] | undefined> {
    try {
        const queryParams = new URLSearchParams();
        queryParams.append("date_from", from);
        queryParams.append("date_to", to);
        if (tag) queryParams.append("tag", tag);
        if (currency) queryParams.append("currency", currency);
        const res = await fetch(`https://fund-forge-api.wealthfarming.org/lumir/economic-calendar?${queryParams.toString()}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${localStorage.getItem("token")}`,
            },
        });
        const data = await res.json();
        return data || undefined;
    } catch (error) {
        console.error(error);
    }
}

export interface FormOption {
    label: string;
    value: string;
    type?: string;
}

/**
 * Lấy cấu hình form field từ mảng form_configs thuộc phase daily_plan
 */
export function getDailyPlanFieldConfig(
    formConfigs: FormFieldConfig[] | undefined,
    fieldKey: string
): FormFieldConfig | undefined {
    if (!formConfigs || !Array.isArray(formConfigs)) return undefined;
    return formConfigs.find(
        (config) =>
            config.field_key === fieldKey &&
            (config.phase === "daily_plan" || !config.phase)
    );
}

/**
 * Bóc tách danh sách options (label, giá trị, loại) từ field_key trong form_configs.daily_plan
 */
export function getDailyPlanOptions(
    formConfigs: FormFieldConfig[] | undefined,
    fieldKey: string
): FormOption[] {
    const config = getDailyPlanFieldConfig(formConfigs, fieldKey);
    if (!config || !config.options) return [];

    // Trường hợp options là mảng string
    if (Array.isArray(config.options)) {
        return config.options.map((opt: any) => {
            if (typeof opt === "string") {
                return {
                    label: opt,
                    value: opt,
                    type: config.input_type || "select",
                };
            }
            // Trường hợp options là mảng object { label, value, type/kind }
            return {
                label: opt.label || opt.name || opt.value || String(opt),
                value: opt.value || opt.id || opt.label || String(opt),
                type: opt.type || opt.kind || config.input_type || "select",
            };
        });
    }

    return [];
}

/**
 * Map giá trị từ UI sang Enum API dựa trên options của Form Config
 */
export function mapUiToApiOption(
    uiValue: string | undefined | null,
    options: FormOption[]
): string | undefined {
    if (!uiValue) return undefined;
    const found = options.find(
        (opt) => opt.value === uiValue || opt.label === uiValue
    );
    return found ? found.value : uiValue;
}

/**
 * Map giá trị từ API về Label hiển thị trên UI dựa trên options của Form Config
 */
export function mapApiToUiOption(
    apiValue: string | undefined | null,
    options: FormOption[]
): string {
    if (!apiValue) return "";
    const found = options.find(
        (opt) => opt.value === apiValue || opt.label === apiValue
    );
    return found ? found.label : apiValue;
}

async function getEndOfDaySummary(account_number: string, date?: string): Promise<EndofDaySummaryAPI | undefined> {
    try {
        const queryParams = new URLSearchParams();
        queryParams.append("account_number", account_number);
        if (date) queryParams.append("date", date);
        const res = await fetch(`${API_URL}/api/v1/be/checklist/eod-summary?${queryParams.toString()}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${localStorage.getItem("token")}`,
            },
        });
        const data: EndofDaySummaryAPI = await res.json();
        return data || undefined;
    } catch (error) {
        console.error(error);
    }
}

async function getToolTip(): Promise<PostTradeTooltipAPIResponse | undefined> {
    try {
        const res = await fetch(`${API_URL}/api/v1/be/tooltips`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${localStorage.getItem("token")}`,
            },
        });
        const data: PostTradeTooltipAPIResponse = await res.json();
        return data || undefined;
    } catch (error) {
        console.error(error);
    }
}

export {
    getDailyChecklist,
    updateDailyChecklist,
    uploadPreTradeChecklist,
    getLiveTradeData,
    getAccountList,
    getHistoryTradeData,
    uploadPicture,
    streamingUploadPicture,
    getJournalCompletionStatus,
    getMostCompleteExecution,
    getExecutionCompletenessScore,
    getTradePositionHistory,
    getPostTradeJournalHistory,
    getEcoCalendar,
    getEndOfDaySummary,
    getToolTip
};